import { useState, useRef, useEffect } from "react";
import ReactTimeago from "react-timeago-i18n";
import {
  Paper,
  Group,
  Stack,
  Textarea,
  Loader,
  Text,
  Container,
  rem,
  Alert,
  Grid,
  Button,
  ScrollAreaAutosize,
  Center,
} from "@mantine/core";
import { useIsomorphicEffect } from "@mantine/hooks";
import { Link } from "wouter";
import { IconBox, IconSend } from "@tabler/icons-react";
import PlagroundThreadList from "./PlaygroundThreadList";
import PlaygroundThreadConfig from "./PlaygroundThreadConfig";
import { useStore } from "../../store";
import { sendChatMessage, getThreadMessages } from "./threadApi";
import ModelSelect from "../../_Common/ModelSelect";
import CitationsList from "../../_Common/CitationsList";

function createUuid() {
  return URL.createObjectURL(new Blob([])).slice(-36);
}

export default function PlaygroundThreadPage() {
  const [isThinking, setIsThinking] = useState(false);
  const [textareaText, setTextareaText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);
  const [messages, setMessages] = useState([]);
  // const [response, setResponse] = useState({});
  // const [supportedModels, setSupportedModels] = useState([]);
  // const [systemPrompt, setSystemPrompt] = useState("");

  const selectedThread = useStore((state) => state.chat.selectedThread);
  const viewport = useRef(null);

  const scrollToBottom = () =>
    viewport.current.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: "smooth",
    });

  //const { start, clear } = useTimeout(() => scrollToBottom(), 1);

  useEffect(() => {
    if (!selectedThread?.id) return;
    setIsLoading(true);
    setMessages([]);
    getThreadMessages(selectedThread?.id).then((data) => {
      setMessages(data.toReversed());
      setIsLoading(false);
      // start();
    });
  }, [selectedThread]);

  useIsomorphicEffect(() => {
    if (viewport.current) scrollToBottom();
  }, [messages]);

  function sendMessage() {
    if (!selectedThread?.id) return;

    setIsThinking(true);
    const msg = { message: textareaText, model: selectedModel };

    const protoMsg = {
      senderType: "client",
      id: createUuid(),
      trigger: "user",
      text: textareaText,
      sent: new Date().toISOString(),
    };

    let newMessages = [...messages, protoMsg];
    setMessages(newMessages);
    sendChatMessage(selectedThread.id, msg)
      .then((response) => {
        // TBD: Add the REQUEST message as well!
        setMessages([...newMessages, response]);
        setTextareaText("");
        setIsThinking(false);
      })
      .catch((error) => {
        console.error(error);
        // setResponse(messageForError(error));
        setIsThinking(false);
      });
  }

  return (
    <Container px={0}>
      <Stack justify="space-between" gap="2px">
        <Alert mb="xl">
          This allows you to have chats with LLMs. The request sent to the LLM
          will include the entire chat history, so requests will be modified
          slightly, contain a system message and become more expensive the
          longer the thread gets. Use <Link to="/bulk">Bulk</Link> if you want
          to check on the exact behavior for unmodified prompts.
        </Alert>

        <Grid>
          <Grid.Col span={4}>
            <ScrollAreaAutosize mah="calc(90dvh - var(180px))">
              <PlagroundThreadList />
            </ScrollAreaAutosize>
          </Grid.Col>
          <Grid.Col span={8}>
            {!selectedThread ? (
              // <div>Please select a thread on the left!</div>
              <PlaygroundThreadConfig />
            ) : (
              <ScrollAreaAutosize
                h="calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 180px)"
                viewportRef={viewport}
              >
                <Group justify="space-around">
                  {isLoading && (
                    <Loader style={{ alignSelf: "center" }} mt="xl" size="xl" />
                  )}
                </Group>
                {messages?.length === 0 && !isLoading && (
                  <Alert>
                    <Center>
                      No messages yet!
                      <br />
                      <IconBox />
                    </Center>
                  </Alert>
                )}
                {messages?.map((message) => (
                  <Paper radius="md" withBorder p="xs" mb="sm" key={message.id}>
                    <Text style={{ whiteSpace: "pre-wrap" }}>
                      {message.text}
                    </Text>
                    <CitationsList citations={message.citations} />
                    <Group justify="flex-end">
                      <Text c="dimmed" fz="sm">
                        <ReactTimeago date={message.sent} />
                      </Text>
                    </Group>
                  </Paper>
                ))}

                <Paper mt="lg">
                  <Stack gap="2px" bg="var(--mantine-color-myColor-light)">
                    <Textarea
                      autosize
                      minRows={1}
                      maxRows={5}
                      radius="md"
                      onChange={(event) =>
                        setTextareaText(event.currentTarget.value)
                      }
                      value={textareaText}
                      style={{ flex: "1" }}
                    />
                    <Group mt="md" justify="flex-end">
                      <ModelSelect setSelectedModel={setSelectedModel} />
                      {/* <Select
                        placeholder="Select model"
                        searchable
                        data={supportedModels}
                        value={selectedModel}
                        onChange={setSelectedModel}
                      /> */}
                      <Button
                        onClick={() => sendMessage()}
                        disabled={
                          isThinking || textareaText === "" || !selectedModel
                        }
                        leftSection={
                          <IconSend
                            style={{ width: rem(18), height: rem(18) }}
                            stroke={1.8}
                          />
                        }
                      >
                        Send
                      </Button>
                    </Group>
                    <Group justify="space-around">
                      {isThinking && (
                        <Loader
                          style={{ alignSelf: "center" }}
                          mt="xl"
                          size="xl"
                        />
                      )}
                    </Group>
                  </Stack>
                </Paper>
              </ScrollAreaAutosize>
            )}
          </Grid.Col>
          {/* <Grid.Col span={3}>
            <Select
              placeholder="Select model"
              data={supportedModels}
              value={selectedModel}
              onChange={setSelectedModel}
            />

            <Textarea
              autosize
              label="System Prompt"
              minRows={3}
              maxRows={8}
              mr="md"
              radius="md"
              size="md"
              onChange={(event) => setSystemPrompt(event.currentTarget.value)}
              value={systemPrompt}
              style={{ flex: "1" }}
            />

            <div>
              USD {response.usage?.costUsd}
              <br />
              model {response.model}
              <br />
              prov {response.provider}
              <br />
              ms {response.milliseconds}
            </div>
          </Grid.Col> */}
        </Grid>
      </Stack>
    </Container>
  );
}
